import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { Wallet } from 'lucide-react';

/**
 * ShippingPoolWalletPanel — iter-379
 *
 * Extracted from VendorWalletLedger.jsx's body so the SAME summary +
 * ledger UI can be reused both as a full page (`/vendor/wallet`) and as
 * a Tab inside the vendor Payouts page — one fetch/render implementation,
 * no duplicated logic.
 */
export default function ShippingPoolWalletPanel() {
  const { apiCall } = useAuth();
  const { formatPrice } = useCurrency();
  const [data, setData] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [w, l] = await Promise.all([
          apiCall('/api/vendor/wallet'),
          apiCall('/api/vendor/wallet/ledger?limit=500'),
        ]);
        setData(w);
        setLedger(l?.ledger || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [apiCall]);

  const w = data?.wallet;
  const fmt = (n) => (formatPrice ? formatPrice(Math.abs(n || 0)) : `₹${Math.abs(n || 0).toFixed(2)}`);

  if (loading) {
    return <div data-testid="wallet-ledger-loading" className="text-sm text-zinc-500 py-6">Loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid="shipping-pool-wallet-panel">
      <section data-testid="wallet-ledger-summary" className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm">
        <div className="text-xs text-zinc-500 uppercase tracking-wide">Current balance</div>
        <div className={`text-4xl font-bold mt-1 ${(w?.balance || 0) < 0 ? 'text-rose-600' : 'text-zinc-900'}`}>
          {(w?.balance || 0) < 0 ? '−' : ''}
          {fmt(w?.balance)}
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          Surplus from Aniyor-managed shipments (the platform's 5% shipping pool contribution left over
          after your customer's shipping was subsidised) accumulates here — used to cushion return /
          RTO reverse-shipping costs and payout deficits. Self-managed shipments never touch this wallet.
        </p>
        <div className="grid grid-cols-3 gap-3 mt-5 text-xs">
          <div className="bg-emerald-50 rounded-lg p-3">
            <div className="text-emerald-700 mb-1">Lifetime credits</div>
            <div className="font-semibold text-zinc-900">{fmt(w?.lifetime_credits)}</div>
          </div>
          <div className="bg-rose-50 rounded-lg p-3">
            <div className="text-rose-700 mb-1">Lifetime debits</div>
            <div className="font-semibold text-zinc-900">{fmt(w?.lifetime_debits)}</div>
          </div>
          <div className="bg-zinc-100 rounded-lg p-3">
            <div className="text-zinc-600 mb-1">Status</div>
            <div className="font-semibold text-zinc-900 capitalize">{w?.status || '—'}</div>
          </div>
        </div>
      </section>

      <section className="bg-white border border-zinc-200 rounded-2xl shadow-sm">
        <header className="px-5 py-3 border-b border-zinc-200 text-sm font-semibold text-zinc-800 flex items-center gap-2">
          <Wallet className="w-4 h-4 text-emerald-600" />
          Ledger — {ledger.length} entries
        </header>
        {ledger.length === 0 ? (
          <div className="p-5 text-sm text-zinc-500" data-testid="wallet-ledger-empty">No wallet activity yet.</div>
        ) : (
          <ul>
            {ledger.map((row) => {
              const isCredit = ['credit', 'payout_recovery', 'activate'].includes(row.txn_type);
              return (
                <li key={row.id} data-testid="wallet-ledger-row" className="px-5 py-3 border-b border-zinc-100 last:border-0 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-800">{row.note || row.txn_type}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {(row.created_at || '').slice(0, 19).replace('T', ' ')}
                      {' • '}
                      {row.txn_type}
                      {row.reference_type ? ` • ${row.reference_type}` : ''}
                    </div>
                  </div>
                  <div className={`text-right text-sm font-semibold tabular-nums whitespace-nowrap ${isCredit ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {isCredit ? '+' : '−'}
                    {fmt(row.amount)}
                    <div className="text-[10px] font-normal text-zinc-400">bal&nbsp;{fmt(row.balance_after)}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
